import {
  addDoc,
  deleteDoc,
  getDoc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  type DocumentData,
} from 'firebase/firestore';
import type { Box, BoxDraft } from '@easymoving/types';
import { boxDoc, boxesCol } from './paths';
import { toMillis } from './serialize';

function boxFromDoc(d: DocumentData, id: string): Box {
  return {
    id,
    name: d.name ?? '',
    room: d.room,
    destination: d.destination,
    progressStatus: d.progressStatus,
    storageLocation: d.storageLocation,
    notes: d.notes,
    qrCodeUrl: d.qrCodeUrl,
    createdAt: toMillis(d.createdAt),
    updatedAt: toMillis(d.updatedAt),
  };
}

export async function createBox(uid: string, draft: BoxDraft): Promise<string> {
  const ref = await addDoc(boxesCol(uid), {
    ...draft,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

export async function getBox(uid: string, boxId: string): Promise<Box | null> {
  const snap = await getDoc(boxDoc(uid, boxId));
  return snap.exists() ? boxFromDoc(snap.data(), snap.id) : null;
}

export async function updateBox(
  uid: string,
  boxId: string,
  patch: Partial<Omit<Box, 'id' | 'createdAt'>>,
): Promise<void> {
  await updateDoc(boxDoc(uid, boxId), { ...patch, updatedAt: serverTimestamp() });
}

export async function deleteBox(uid: string, boxId: string): Promise<void> {
  await deleteDoc(boxDoc(uid, boxId));
}

export function subscribeBoxes(
  uid: string,
  cb: (boxes: Box[]) => void,
  onError?: (e: Error) => void,
): () => void {
  const q = query(boxesCol(uid), orderBy('createdAt', 'desc'));
  return onSnapshot(
    q,
    (snap) => cb(snap.docs.map((d) => boxFromDoc(d.data(), d.id))),
    (err) => onError?.(err),
  );
}
